import { getFunctions, httpsCallable } from 'firebase/functions';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '../config/firebase';

// 🔐 SERVICE CUSTOM CLAIMS - FRONTEND
// Gestion sécurisée des rôles utilisateurs côté client

class CustomClaimsService {
  constructor() {
    this.functions = getFunctions();
    this.currentUserClaims = null;
    this.fallbackRole = null;
    this.listeners = new Set();
    
    // Écouter les changements de token pour mettre à jour les claims
    this.initializeClaimsListener();
  }

  /**
   * Initialise l'écoute des changements de Custom Claims
   */
  initializeClaimsListener() {
    onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          // Forcer le rafraîchissement du token pour obtenir les derniers claims
          const idTokenResult = await user.getIdTokenResult(true);
          this.currentUserClaims = idTokenResult.claims;
          
          // Notifier tous les listeners
          this.notifyListeners(this.currentUserClaims);
        } catch (error) {
          console.error('Erreur lors de la récupération des claims:', error);
          this.currentUserClaims = null;
          this.notifyListeners(null);
        }
      } else {
        this.currentUserClaims = null;
        this.notifyListeners(null);
      }
    });
  }

  /**
   * Notifie tous les listeners des changements de claims
   */
  notifyListeners(claims) {
    this.listeners.forEach(listener => {
      try {
        listener(claims);
      } catch (error) {
        console.error('Erreur dans un listener de claims:', error);
      }
    });
  }

  setFallbackRole(role = null) {
    this.fallbackRole = role || null;
    if (!this.currentUserClaims && this.fallbackRole) {
      this.notifyListeners({ role: this.fallbackRole });
    }
  }

  /**
   * Ajoute un listener pour les changements de claims
   * @param {Function} listener - Fonction appelée lors des changements
   * @returns {Function} Fonction pour supprimer le listener
   */
  addClaimsListener(listener) {
    this.listeners.add(listener);
    
    // Appeler immédiatement avec les claims actuels
    if (this.currentUserClaims !== null) {
      listener(this.currentUserClaims);
    }
    
    // Retourner une fonction de nettoyage
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Obtient les Custom Claims de l'utilisateur actuel
   * @param {boolean} forceRefresh - Forcer le rafraîchissement du token
   * @returns {Promise<Object|null>} Claims de l'utilisateur
   */
  async getCurrentUserClaims(forceRefresh = false) {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    try {
      const idTokenResult = await user.getIdTokenResult(forceRefresh);
      this.currentUserClaims = idTokenResult.claims;
      return idTokenResult.claims;
    } catch (error) {
      console.error('Erreur lors de la récupération des claims:', error);
      return null;
    }
  }

  /**
   * Vérifie si l'utilisateur actuel a un rôle spécifique
   * @param {string} role - Rôle à vérifier ('admin' ou 'reseller')
   * @returns {boolean} True si l'utilisateur a le rôle
   */
  hasRole(role) {
    const currentRole = this.currentUserClaims?.role || this.fallbackRole || null;
    if (!currentRole) {
      return false;
    }
    return currentRole === role;
  }

  /**
   * Vérifie si l'utilisateur actuel est administrateur
   * @returns {boolean} True si admin
   */
  isAdmin() {
    return this.hasRole('admin');
  }

  /**
   * Vérifie si l'utilisateur actuel est revendeur
   * @returns {boolean} True si reseller
   */
  isReseller() {
    return this.hasRole('reseller');
  }

  /**
   * Vérifie si l'utilisateur a au moins un des rôles spécifiés
   * @param {string[]} roles - Liste des rôles acceptés
   * @returns {boolean} True si l'utilisateur a au moins un rôle
   */
  hasAnyRole(roles) {
    const currentRole = this.currentUserClaims?.role || this.fallbackRole || null;
    if (!currentRole) {
      return false;
    }
    return roles.includes(currentRole);
  }

  /**
   * Obtient le rôle de l'utilisateur actuel
   * @returns {string|null} Rôle de l'utilisateur ou null
   */
  getCurrentUserRole() {
    return this.currentUserClaims?.role || this.fallbackRole || null;
  }

  // 🔧 FONCTIONS D'ADMINISTRATION (Cloud Functions)

  /**
   * Attribue un rôle à un utilisateur (ADMIN SEULEMENT)
   * @param {string} targetUserId - ID de l'utilisateur cible
   * @param {string|null} role - Rôle à attribuer ('admin', 'reseller', ou null)
   * @returns {Promise<Object>} Résultat de l'opération
   */
  async setUserRole(targetUserId, role) {
    if (!this.isAdmin()) {
      throw new Error('Seuls les administrateurs peuvent modifier les rôles');
    }

    try {
      const setUserRoleFunction = httpsCallable(this.functions, 'setUserRole');
      const result = await setUserRoleFunction({ targetUserId, role });
      
      // Forcer le rafraîchissement des claims si c'est l'utilisateur actuel
      if (targetUserId === auth.currentUser?.uid) {
        await this.getCurrentUserClaims(true);
      }
      
      return result.data;
    } catch (error) {
      console.error('Erreur lors de l\'attribution du rôle:', error);
      throw new Error(error.message || 'Erreur lors de l\'attribution du rôle');
    }
  }

  /**
   * Obtient les informations de rôle d'un utilisateur
   * @param {string} userId - ID de l'utilisateur (optionnel)
   * @returns {Promise<Object>} Informations de l'utilisateur
   */
  async getUserRole(userId = null) {
    try {
      const getUserRoleFunction = httpsCallable(this.functions, 'getUserRole');
      const result = await getUserRoleFunction({ userId });
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du rôle:', error);
      throw new Error(error.message || 'Erreur lors de la récupération du rôle');
    }
  }

  /**
   * Liste tous les utilisateurs avec leurs rôles (ADMIN SEULEMENT)
   * @param {number} limit - Nombre maximum d'utilisateurs
   * @param {string} pageToken - Token de pagination
   * @returns {Promise<Object>} Liste des utilisateurs
   */
  async listUsersWithRoles(limit = 100, pageToken = null) {
    if (!this.isAdmin()) {
      throw new Error('Seuls les administrateurs peuvent lister les utilisateurs');
    }

    try {
      const listUsersFunction = httpsCallable(this.functions, 'listUsersWithRoles');
      const result = await listUsersFunction({ limit, pageToken });
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la liste des utilisateurs:', error);
      throw new Error(error.message || 'Erreur lors de la liste des utilisateurs');
    }
  }

  /**
   * Révoque tous les tokens d'un utilisateur (ADMIN SEULEMENT)
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} Résultat de l'opération
   */
  async revokeUserTokens(userId) {
    if (!this.isAdmin()) {
      throw new Error('Seuls les administrateurs peuvent révoquer les tokens');
    }

    try {
      const revokeTokensFunction = httpsCallable(this.functions, 'revokeUserTokens');
      const result = await revokeTokensFunction({ userId });
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la révocation des tokens:', error);
      throw new Error(error.message || 'Erreur lors de la révocation des tokens');
    }
  }

  async saveOperatorTelegramConfig(targetUserId, { botToken = '', chatId = '', enabled = true } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Seuls les administrateurs peuvent configurer Telegram pour un operateur');
    }

    try {
      const saveOperatorTelegramConfigFunction = httpsCallable(this.functions, 'saveOperatorTelegramConfig');
      const result = await saveOperatorTelegramConfigFunction({
        targetUserId,
        botToken,
        chatId,
        enabled,
      });
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde Telegram operateur:', error);
      throw new Error(error.message || 'Erreur lors de la sauvegarde Telegram operateur');
    }
  }

  async deleteOperatorTelegramConfig(targetUserId) {
    if (!this.isAdmin()) {
      throw new Error('Seuls les administrateurs peuvent supprimer Telegram pour un operateur');
    }

    try {
      const deleteOperatorTelegramConfigFunction = httpsCallable(this.functions, 'deleteOperatorTelegramConfig');
      const result = await deleteOperatorTelegramConfigFunction({ targetUserId });
      return result.data;
    } catch (error) {
      console.error('Erreur lors de la suppression Telegram operateur:', error);
      throw new Error(error.message || 'Erreur lors de la suppression Telegram operateur');
    }
  }

  // 🔄 UTILITAIRES

  /**
   * Attend que les Custom Claims soient chargés
   * @param {number} timeout - Timeout en millisecondes
   * @returns {Promise<Object|null>} Claims ou null si timeout
   */
  async waitForClaims(timeout = 5000) {
    return new Promise((resolve) => {
      if (this.currentUserClaims !== null) {
        resolve(this.currentUserClaims);
        return;
      }

      const timeoutId = setTimeout(() => {
        resolve(null);
      }, timeout);

      const unsubscribe = this.addClaimsListener((claims) => {
        if (claims !== null) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(claims);
        }
      });
    });
  }

  /**
   * Nettoie les listeners (à appeler lors du démontage)
   */
  cleanup() {
    this.listeners.clear();
  }
}

// Instance singleton
const customClaimsService = new CustomClaimsService();

export default customClaimsService;

// 📝 UTILISATION :
// 
// import customClaimsService from './services/customClaimsService';
// 
// // Vérifier le rôle
// if (customClaimsService.isAdmin()) {
//   // Code admin
// }
// 
// // Écouter les changements de rôle
// const unsubscribe = customClaimsService.addClaimsListener((claims) => {
//   console.log('Nouveau rôle:', claims?.role);
// });
// 
// // Attribuer un rôle (admin seulement)
// await customClaimsService.setUserRole('userId123', 'reseller');
// 
// // Nettoyer lors du démontage
// unsubscribe();

// 🔐 SÉCURITÉ :
// - Toutes les vérifications côté client sont indicatives
// - La sécurité réelle est assurée par les règles Firestore et Cloud Functions
// - Les Custom Claims sont signés par Firebase et ne peuvent pas être falsifiés
